import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import {
  LinkAuthenticationElement,
  AddressElement,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import {
  StripeAddressElement,
  StripeAddressElementChangeEvent,
  StripeAddressElementOptions,
  StripeLinkAuthenticationElementChangeEvent,
} from '@stripe/stripe-js';
import useCheckoutV2 from '../../hooks/useCheckoutV2';
import ActionContainer from '../ActionContainer';
import MarketingOptInCheckbox from '../MarketingOptInCheckbox';
import Button from '../Button';
import ErrorMessage, { useError } from '../ErrorMessage';
import PayPalButton from '../PayPalButton';
import { ReactComponent as PayPalIcon } from '../../assets/images/icons/paypal.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/icons/info_outline_24.svg';
import { useCartPricingError } from '../../contexts/CartPricingContext';
import useHardwareFirstApplePayOption from '../../hooks/useHardwareFirstApplePayOption';
import { applePayWalletOptions } from '../../lib/applePayRecurring';
import { PlacesAutocompleteInput } from '../PlacesAutocompleteInput';
import { PlaceAddress } from '../../lib/placesAutocomplete';
import { logInternalError } from '../../lib/errors';
import * as events from '../../lib/analytics/events';
import hotjar from '../../lib/analytics/HotJar';
import { useLocale } from '../../contexts/LocaleContext';
import type { PayPalSubmission } from '../../hooks/usePayPalInlineCheckout';
import type { StripeCheckoutPrefill } from '../../hooks/useRescueCheckoutPrefill';
import {
  GiftOptionsValue,
  billsBuyerOnToken,
  giftCardDeliveryErrors,
  giftOptionsErrors,
  giftOptionsValid,
  toGiftRecipientInput,
} from '../../lib/giftRecipient';
import { GiftRecipientInput, GiftShipsTo } from '../../types/gql-op-types';
import GiftOptions from './GiftOptions';
import { firstIncompleteSection } from './checkoutReadiness';
import { buyerBillingMissing, confirmationBillingDetails } from './buyerBilling';
import GiftBillingAddress from './GiftBillingAddress';
import GiftCardFields from './GiftCardFields';
import giftStyles from './GiftOptions.module.scss';

import { shouldOfferPlacesAutocomplete } from '../../lib/addressAutocompleteMode';
import { getGoogleMapsApiKey } from '../../lib/stripeConfig';
import StripeElementsProvider, { HARDWARE_FIRST_GIFT_APPEARANCE } from '../../lib/StripeElementsProvider';
import ShippingOptionsList from '../../views/Checkout/components/ShippingOptionsList';
import * as types from '../../types';
import StripeCheckoutSubmitContext, { STRIPE_CHECKOUT_FORM_ID } from './StripeCheckoutSubmitContext';
import styles from './StripePaymentForm.module.scss';

/** v2 labels the submit for the wallet that will take over from it; card keeps "Place order" */
const WALLET_SUBMIT_KEYS: Record<string, string> = {
  apple_pay: 'hardware_first_checkout_buy_with_apple_pay',
  google_pay: 'hardware_first_checkout_buy_with_google_pay',
  link: 'hardware_first_checkout_pay_with_link',
  klarna: 'hardware_first_checkout_continue_with_klarna',
};

export type StripeAddressValue = StripeAddressElementChangeEvent['value'];

export type StripePaymentMethodDetails =
  | { type: 'card'; brand: string; last4: string; expMonth: number; expYear: number }
  | { type: 'us_bank_account'; bankName: string; last4: string; accountType: string }
  | { type: 'sepa_debit'; bankName: string; last4: string; country: string };

/** @deprecated use StripePaymentMethodDetails instead */
export type StripeCardDetails = StripePaymentMethodDetails;

export interface StripeCheckoutSubmission {
  tokenId: string;
  email: string;
  address: StripeAddressValue;
  shippingCode: types.ShippingCode;
  paymentMethodDetails: StripePaymentMethodDetails;
  /** true when payment method has identity verification (apple pay, google pay, stripe link) */
  isIdentityVerified: boolean;
  /** analytics payment type — distinguishes card vs wallet vs bank */
  paymentType: events.PaymentType;
  /**
   * true when the token was minted in payment mode: it goes to the purchase mutation and recurly
   * charges it inline instead of storing it first at the checkout endpoint
   */
  chargedInline: boolean;
  /** undefined is the form; the express row says so */
  source?: events.CheckoutSurface;
  giftRecipient?: GiftRecipientInput;
  /** the buyer's own billing address on a ship-to-recipient gift; the shipping address is the recipient's */
  billingAddress?: StripeAddressValue;
}

interface StripePaymentFormProps {
  /**
   * Focus the email field as soon as it mounts, which also scrolls it into view. On by default for the
   * checkout page, where the form is the page; off where the form sits below content the shopper
   * should read first.
   */
  autoFocusEmail?: boolean;
  /**
   * Called after successful form submission with all checkout data collected by the form.
   * The form owns all field state internally via refs — the parent never needs to read
   * stale React state from a closure.
   */
  onSubmitCheckout: (submission: StripeCheckoutSubmission) => void;
  /** called when the address element value changes (for derived state like canExpedite) */
  onAddressChange?: (address: StripeAddressValue) => void;
  /** whether any cart item is a preorder (affects shipping options) */
  anyPreorder: boolean;
  /**
   * whether the cart contains physical products that ship. when false (digital-only
   * carts, e.g. gift cards) the shipping method selector is hidden - picking a
   * shipping speed makes no sense for a digital order. defaults to true.
   */
  requiresShippingDetails?: boolean;
  /** whether the shipping address supports expedited shipping */
  canExpedite: boolean;
  /** currently selected shipping code */
  selectedShippingCode: types.ShippingCode | undefined;
  /** called when shipping speed selection changes */
  onShippingCodeChange?: (shippingCode: types.ShippingCode) => void;
  /** external submitting state (e.g. parent is saving billing info) */
  submitting?: boolean;
  /** external error message to display */
  serverError?: string;
  /** called when email field becomes valid — used for proactive account-exists check */
  onEmailComplete?: (email: string) => void;
  /**
   * called when the email field loses focus holding a valid address. unlike onEmailComplete this
   * does not fire for every valid prefix typed on the way to the final address, so it's the hook
   * for side effects that must not run on a stranger's email (the abandoned-cart capture)
   */
  onEmailBlur?: (email: string) => void;
  /** show paypal as an accordion item below stripe's PaymentElement */
  showPayPal?: boolean;
  /** called when paypal popup completes with a billing token + collected form data */
  onPayPalComplete?: (submission: PayPalSubmission) => void;
  /**
   * cart total for the payment-mode flip. the element mounts in setup mode (store-then-charge
   * tokens for card/ach/link) and flips to payment mode with this amount while a method that is
   * charged inline at purchase is selected: klarna always, apple pay / google pay when
   * `walletChargesInline` is set. undefined disables the flip entirely.
   */
  inlineChargeAmountInCents?: number;
  /**
   * hardware-first carts: apple pay / google pay charge inline so the wallet sheet shows the
   * tracker total and the card is authorised once instead of verified and then charged off-session
   */
  walletChargesInline?: boolean;
  /**
   * values the email and address elements open with, still editable. stripe reads them only on
   * mount, so the caller has to have them before the form renders.
   */
  prefill?: StripeCheckoutPrefill;
  giftOrder?: boolean;
  /** the gift order also ships things that are not gifts */
  giftMixed?: boolean;
  giftCards?: types.GiftCardCartItem[];
  /** each gift card's formatted amount, by cart item id */
  giftCardAmounts?: Record<string, string>;
  onGiftCardDeliveryChange?: (cartItemId: string, delivery: types.GiftCardDelivery) => void;
}

export default function StripePaymentForm({
  onSubmitCheckout,
  onAddressChange,
  anyPreorder,
  canExpedite,
  requiresShippingDetails = true,
  selectedShippingCode,
  onShippingCodeChange,
  submitting: externalSubmitting = false,
  serverError,
  onEmailComplete,
  onEmailBlur,
  showPayPal = false,
  onPayPalComplete,
  inlineChargeAmountInCents,
  walletChargesInline = false,
  prefill,
  giftOrder = false,
  giftMixed = false,
  giftCards = [],
  giftCardAmounts = {},
  onGiftCardDeliveryChange,
  autoFocusEmail = true,
}: StripePaymentFormProps) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const { country } = useLocale();

  const [internalSubmitting, setInternalSubmitting] = useState(false);
  const { error: internalError, errorID, setError, clearError } = useError();

  const [stripeReady, setStripeReady] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [addressComplete, setAddressComplete] = useState(false);
  // shipping phone, fed to the PaymentElement so Link's sign-up form starts filled in
  const [addressPhone, setAddressPhone] = useState('');
  const [hasShippingCode, setHasShippingCode] = useState(false);

  // the element mode must match the token we mint. cards/ach/link need setup-mode tokens (recurly
  // stores them as billing info, then charges), while a method charged inline at purchase needs a
  // payment-mode token with the real amount: klarna always (its one-time token cannot be stored,
  // and a setup-mode klarna token becomes a recurring mandate), wallets on hardware-first carts
  // (the sheet shows the total, and one auth instead of a verify plus an off-session charge).
  // so the element mounts in setup mode and flips per the selected method.
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState<string | null>(null);
  const appliedElementsModeRef = useRef<'setup' | 'payment'>('setup');
  const chargesInline =
    selectedPaymentMethodType === 'klarna' ||
    (walletChargesInline && (selectedPaymentMethodType === 'apple_pay' || selectedPaymentMethodType === 'google_pay'));
  useEffect(() => {
    if (!elements || inlineChargeAmountInCents === undefined) {
      return;
    }
    if (chargesInline) {
      elements.update({ mode: 'payment', amount: inlineChargeAmountInCents });
      appliedElementsModeRef.current = 'payment';
    } else if (appliedElementsModeRef.current === 'payment') {
      elements.update({ mode: 'setup' });
      appliedElementsModeRef.current = 'setup';
    }
  }, [elements, inlineChargeAmountInCents, chargesInline]);

  // track whether the full payment accordion is visible. starts hidden so a prefilled
  // saved card doesn't show paypal. after a short settle period, if no saved card loaded
  // (complete was never true), we open the accordion. once open, stays open.
  const [paymentAccordionOpen, setPaymentAccordionOpen] = useState(false);
  const hasSavedPaymentRef = useRef(false);
  const settledRef = useRef(false);

  const emailSectionRef = useRef<HTMLDivElement>(null);
  const addressSectionRef = useRef<HTMLDivElement>(null);
  const shippingSectionRef = useRef<HTMLDivElement>(null);
  const paymentSectionRef = useRef<HTMLDivElement>(null);
  const giftSectionRef = useRef<HTMLDivElement>(null);

  const [gift, setGift] = useState<GiftOptionsValue>({ shipsTo: GiftShipsTo.RECIPIENT, email: '', message: '' });
  const giftRef = useRef(gift);
  const giftBillingSectionRef = useRef<HTMLDivElement>(null);
  const giftBillingRef = useRef<StripeAddressValue | undefined>(undefined);
  const giftBillingCompleteRef = useRef(false);
  const [giftBillingComplete, setGiftBillingComplete] = useState(false);
  // a ship-to-recipient gift with this box checked ships to the buyer's billing address
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const sameAsBillingRef = useRef(false);
  const handleGiftBillingChange = useCallback(
    (value: StripeAddressValue, complete: boolean) => {
      giftBillingRef.current = value;
      giftBillingCompleteRef.current = complete;
      // link's sign-up form is the buyer's, so it takes the billing phone, never the recipient's
      setAddressPhone(value.phone ?? '');
      setGiftBillingComplete(complete);
      if (sameAsBillingRef.current) onAddressChange?.(value);
    },
    [onAddressChange],
  );
  const handleSameAsBillingChange = useCallback(
    (checked: boolean) => {
      sameAsBillingRef.current = checked;
      setSameAsBilling(checked);
      const shippingAddress = checked ? giftBillingRef.current : addressRef.current;
      if (shippingAddress) onAddressChange?.(shippingAddress);
    },
    [onAddressChange],
  );
  const handleGiftChange = useCallback((value: GiftOptionsValue) => {
    // the billing element remounts empty when the shopper switches back to shipping to the recipient
    if (value.shipsTo !== giftRef.current.shipsTo) {
      giftBillingRef.current = undefined;
      giftBillingCompleteRef.current = false;
      setGiftBillingComplete(false);
      // the shipping element remounts empty too, in or out of link's elements group
      addressRef.current = undefined;
      addressCompleteRef.current = false;
      setAddressComplete(false);
    }
    giftRef.current = value;
    setGift(value);
  }, []);
  const [giftEmailTouched, setGiftEmailTouched] = useState(false);
  const giftValid = !giftOrder || giftOptionsValid(gift);
  const billsBuyer = giftOrder && billsBuyerOnToken(gift);
  const shipsToBilling = billsBuyer && sameAsBilling;
  const giftCardsValid = giftCards.every(
    (card) => Object.keys(giftCardDeliveryErrors(card.giftCardDelivery)).length === 0,
  );
  const [giftCardErrorsShown, setGiftCardErrorsShown] = useState(false);
  const giftCardSectionRef = useRef<HTMLDivElement>(null);
  // the billing address is the buyer's own when nothing ships
  const cardOnly = !requiresShippingDetails && giftCards.length > 0;

  // Track auto-fill state to detect Link logout and force remount with correct country defaults.
  // Stripe's defaultValues only apply on mount, so when Link clears auto-filled fields,
  // the country resets to US instead of the fi_country cookie value.
  // Only AddressElement needs remounting — PaymentElement has its own stable country default.
  const [addressKey, setAddressKey] = useState(0);
  const wasAutoFilledRef = useRef(false);

  // google places search offered above stripe's form for countries stripe can't
  // autocomplete (e.g. FI, MT). Purely additive — the form below is always rendered.
  const offerPlacesAutocomplete = shouldOfferPlacesAutocomplete(country);
  const mapsApiKey = getGoogleMapsApiKey();
  // outside a payment element's group, stripe autocompletes only with our own maps key
  const addressAutocomplete: StripeAddressElementOptions['autocomplete'] = offerPlacesAutocomplete
    ? { mode: 'disabled' }
    : billsBuyer && mapsApiKey
      ? { mode: 'google_maps_api', apiKey: mapsApiKey }
      : { mode: 'automatic' };
  const recipientAddressElementRef = useRef<StripeAddressElement | null>(null);
  const checkoutV2 = useCheckoutV2();
  const sectionClass = classNames(styles.section, { [styles.sectionCard]: checkoutV2 });
  const [placesAddress, setPlacesAddress] = useState<PlaceAddress | undefined>();
  const [placesInputVisible, setPlacesInputVisible] = useState(true);

  const handlePlacesSelect = useCallback((address: PlaceAddress) => {
    setPlacesAddress(address);
    setPlacesInputVisible(false);
    setAddressKey((k) => k + 1);
  }, []);

  const handleSearchAgain = useCallback(() => {
    setPlacesInputVisible(true);
  }, []);

  // Refs hold the latest form values for submission — immune to React 18's automatic batching.
  // State updates from onChange may not flush before the async submit chain reads them, but
  // refs are always current because they're updated synchronously in the onChange handler.
  const emailRef = useRef('');
  const emailCompleteRef = useRef(false);
  const addressRef = useRef<StripeAddressValue | undefined>(undefined);
  const shippingCodeRef = useRef<types.ShippingCode | undefined>(undefined);
  const addressCompleteRef = useRef(false);

  /** the address the order ships to, once it is complete */
  const completeShippingAddress = useCallback((currentGift: GiftOptionsValue | undefined) => {
    if (billsBuyerOnToken(currentGift) && sameAsBillingRef.current) {
      return giftBillingCompleteRef.current ? giftBillingRef.current : undefined;
    }
    return addressCompleteRef.current ? addressRef.current : undefined;
  }, []);

  const [paypalSelected, setPaypalSelected] = useState(false);

  const handlePayPalTokenReceived = useCallback(
    (token: types.BillingToken) => {
      const currentShippingCode = shippingCodeRef.current;
      const currentGift = giftOrder ? giftRef.current : undefined;
      const currentAddress = completeShippingAddress(currentGift);
      if (
        !currentShippingCode ||
        !currentAddress ||
        (currentGift && !giftOptionsValid(currentGift)) ||
        !giftCardsValid ||
        buyerBillingMissing(currentGift, giftBillingRef.current)
      ) {
        return;
      }
      onPayPalComplete?.({
        paypalTokenId: token.id,
        email: emailRef.current,
        address: currentAddress,
        shippingCode: currentShippingCode,
        giftRecipient: currentGift && toGiftRecipientInput(currentGift),
        billingAddress: billsBuyerOnToken(currentGift) ? giftBillingRef.current : undefined,
      });
    },
    [onPayPalComplete, giftOrder, giftCardsValid, completeShippingAddress],
  );

  const pricingError = useCartPricingError();
  const { applePayRecurring, trialPending } = useHardwareFirstApplePayOption();
  const isSubmitting = internalSubmitting || externalSubmitting;
  // pricingError shadows serverError/internalError: if Recurly couldn't
  // calculate pricing, the customer needs to fix that first.
  const displayError = pricingError || serverError || internalError;

  // spread into the address defaults; undefined leaves the options exactly as they were without one
  const prefillContact = prefill
    ? { firstName: prefill.firstName, lastName: prefill.lastName, phone: prefill.phone }
    : undefined;

  const handleAddressChange = useCallback(
    (event: StripeAddressElementChangeEvent) => {
      const isEmpty = !event.value.address.line1 && !event.value.name;
      if (event.complete) {
        wasAutoFilledRef.current = true;
      } else if (wasAutoFilledRef.current && isEmpty) {
        wasAutoFilledRef.current = false;
        addressCompleteRef.current = false;
        addressRef.current = undefined;
        setAddressKey((k) => k + 1);
      }
      addressRef.current = event.value;
      addressCompleteRef.current = event.complete;
      if (!billsBuyer) setAddressPhone(event.value.phone ?? '');
      setAddressComplete(event.complete);
      onAddressChange?.(event.value);
    },
    [billsBuyer, onAddressChange],
  );

  const [emailComplete, setEmailComplete] = useState(false);
  const lastCapturedEmailRef = useRef('');

  const handleEmailChange = useCallback(
    (event: StripeLinkAuthenticationElementChangeEvent) => {
      emailRef.current = event.value.email;
      emailCompleteRef.current = event.complete;
      if (event.complete && event.value.email !== lastCapturedEmailRef.current) {
        lastCapturedEmailRef.current = event.value.email;
        events.checkoutFunnel.emailCaptured(event.value.email);
      }
      setEmailComplete(event.complete);
      if (event.complete) {
        onEmailComplete?.(event.value.email);
      }
    },
    [onEmailComplete],
  );

  const handleEmailBlur = useCallback(() => {
    if (emailCompleteRef.current) {
      onEmailBlur?.(emailRef.current);
    }
  }, [onEmailBlur]);

  const handleShippingCodeChange = useCallback(
    (code: types.ShippingCode) => {
      shippingCodeRef.current = code;
      setHasShippingCode(true);
      onShippingCodeChange?.(code);
    },
    [onShippingCodeChange],
  );

  const scrollToRef = useCallback((ref: React.RefObject<HTMLDivElement | null>) => {
    requestAnimationFrame(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, []);

  // the accordion's wallet sheets collect no shipping address, so a wallet row is only offered once
  // the form has what the token needs; the submit path checks the same fields
  const incompleteSection = firstIncompleteSection({
    emailComplete,
    giftValid,
    giftCardsValid,
    addressComplete: shipsToBilling || addressComplete,
    billingComplete: !billsBuyer || giftBillingComplete,
    hasShippingCode,
  });
  const walletsReady = !incompleteSection;
  // stripe reads `wallets` once at creation, so the rows appear by remounting the element the first
  // time the form is ready. a card already typed in full is kept: that shopper stays on card
  const [walletsRevealed, setWalletsRevealed] = useState(false);
  // the section keeps its height while the new element loads, so the buttons below it do not jump
  const paymentElementRef = useRef<HTMLDivElement>(null);
  const [heldHeight, setHeldHeight] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (walletsReady && !walletsRevealed && !(paymentComplete && selectedPaymentMethodType === 'card')) {
      setHeldHeight(paymentElementRef.current?.offsetHeight || undefined);
      setWalletsRevealed(true);
    }
  }, [walletsReady, walletsRevealed, paymentComplete, selectedPaymentMethodType]);
  // paypal's popup needs the same fields, so its row is hidden until the form is ready. a
  // selection is dropped when the form stops being ready, since a selection hides the submit button
  useEffect(() => {
    if (!walletsReady) setPaypalSelected(false);
  }, [walletsReady]);

  const submitForm = useCallback(async () => {
    if (!stripe || !elements || isSubmitting) {
      return;
    }

    // validate sections in order and scroll to the first incomplete one
    if (incompleteSection === 'email') {
      scrollToRef(emailSectionRef);
      await elements.submit();
      return;
    }
    if (incompleteSection === 'gift') {
      setGiftEmailTouched(true);
      scrollToRef(giftSectionRef);
      return;
    }
    if (incompleteSection === 'giftCards') {
      setGiftCardErrorsShown(true);
      scrollToRef(giftCardSectionRef);
      return;
    }
    if (incompleteSection === 'address') {
      scrollToRef(addressSectionRef);
      // getValue shows the field errors; elements.submit only validates link's group
      await (recipientAddressElementRef.current?.getValue() ?? elements.submit());
      return;
    }
    if (incompleteSection === 'billing') {
      scrollToRef(giftBillingSectionRef);
      await elements.submit();
      return;
    }
    if (incompleteSection === 'shipping') {
      scrollToRef(shippingSectionRef);
      setError(t('please_select_a_shipping_method'));
      return;
    }
    if (!paymentComplete) {
      scrollToRef(paymentSectionRef);
      await elements.submit();
      return;
    }

    const currentShippingCode = shippingCodeRef.current;
    if (!currentShippingCode) {
      setError(t('please_select_a_shipping_method'));
      return;
    }

    const submitGift = giftOrder ? giftRef.current : undefined;
    const currentAddress = completeShippingAddress(submitGift);
    if (!currentAddress) {
      setError(t('please_enter_your_address'));
      return;
    }

    if (buyerBillingMissing(submitGift, giftBillingRef.current)) {
      scrollToRef(giftBillingSectionRef);
      setError(t('please_enter_your_address'));
      return;
    }
    const billingAddress = billsBuyerOnToken(submitGift) ? giftBillingRef.current : undefined;

    clearError();
    setInternalSubmitting(true);

    try {
      // validate all elements before creating the confirmation token
      const { error: submitError } = await elements.submit();
      if (submitError) {
        // form validation errors (incomplete fields, missing phone, etc.) go to analytics only —
        // these are expected user interactions, not system errors. mirrors the old checkout's
        // events.shipping.continueError() pattern.
        events.shipping.continueError(submitError.message);
        setError(submitError.message ?? t('something_went_wrong'));
        return;
      }

      const { error: tokenError, confirmationToken } = await stripe.createConfirmationToken({
        elements,
        params: {
          return_url: `${window.location.origin}/checkout/stripe`,
          // required because the PaymentElement is configured with fields.billingDetails 'never':
          // the billing details on the payment method come from our own form. redirect methods
          // (klarna) read these on their hosted page, so the customer's contact info is prefilled.
          payment_method_data: {
            billing_details: confirmationBillingDetails(emailRef.current, currentAddress, billingAddress),
          },
          // the link group's shipping-mode element is the buyer's billing address, so the order's shipping goes explicitly
          ...(billsBuyerOnToken(submitGift) && {
            shipping: { name: currentAddress.name, phone: currentAddress.phone, address: currentAddress.address },
          }),
        },
      });

      if (tokenError) {
        // token creation errors are payment-level failures — track in both analytics and sentry
        events.payment.continueError(tokenError.message);
        logInternalError(new Error(tokenError.message ?? 'unknown'), {
          fingerprint: ['{{ default }}', tokenError.message ?? 'unknown'],
          tags: { checkoutStep: 'stripeCheckoutTokenCreation' },
        });
        setError(tokenError.message ?? t('something_went_wrong'));
        return;
      }

      const preview = confirmationToken.payment_method_preview;
      const paymentMethodDetails: StripePaymentMethodDetails =
        preview?.type === 'us_bank_account'
          ? {
              type: 'us_bank_account',
              bankName: preview.us_bank_account?.bank_name ?? '',
              last4: preview.us_bank_account?.last4 ?? '',
              accountType: preview.us_bank_account?.account_type ?? '',
            }
          : preview?.type === 'sepa_debit'
            ? {
                type: 'sepa_debit',
                bankName: preview.sepa_debit?.bank_code ?? '',
                last4: preview.sepa_debit?.last4 ?? '',
                country: preview.sepa_debit?.country ?? '',
              }
            : {
                type: 'card',
                brand: preview?.card?.brand ?? '',
                last4: preview?.card?.last4 ?? '',
                expMonth: preview?.card?.exp_month ?? 0,
                expYear: preview?.card?.exp_year ?? 0,
              };

      // detect identity-verified payment methods (apple pay, google pay, stripe link)
      const walletType = preview?.card?.wallet?.type;
      const isIdentityVerified =
        preview?.type === 'link' || walletType === 'apple_pay' || walletType === 'google_pay' || walletType === 'link';

      const paymentType: events.PaymentType =
        preview?.type === 'klarna'
          ? 'klarna'
          : preview?.type === 'link'
            ? 'link'
            : preview?.type === 'paypal'
              ? 'paypal'
              : walletType === 'apple_pay'
                ? 'apple_pay'
                : walletType === 'google_pay'
                  ? 'google_pay'
                  : walletType === 'link'
                    ? 'link'
                    : preview?.type === 'us_bank_account'
                      ? 'ach'
                      : 'credit_card';

      onSubmitCheckout({
        tokenId: confirmationToken.id,
        email: emailRef.current,
        address: currentAddress,
        shippingCode: currentShippingCode,
        paymentMethodDetails,
        isIdentityVerified,
        paymentType,
        chargedInline: appliedElementsModeRef.current === 'payment',
        // the gift choice captured before the token request, so a switch mid-flight cannot pair with the wrong address
        giftRecipient: submitGift ? toGiftRecipientInput(submitGift) : undefined,
        billingAddress,
      });
    } catch (err) {
      logInternalError(err, { tags: { component: 'StripePaymentForm' } });
      setError(t('something_went_wrong'));
    } finally {
      setInternalSubmitting(false);
    }
  }, [
    stripe,
    elements,
    isSubmitting,
    incompleteSection,
    giftOrder,
    paymentComplete,
    completeShippingAddress,
    scrollToRef,
    clearError,
    setError,
    onSubmitCheckout,
    t,
  ]);

  const walletSubmitKey =
    checkoutV2 && selectedPaymentMethodType ? WALLET_SUBMIT_KEYS[selectedPaymentMethodType] : undefined;
  const submitLabel = walletSubmitKey ? t(walletSubmitKey) : t('place_order');
  const addressHeadingKey = cardOnly
    ? 'hardware_first_checkout_gift_billing_address'
    : !giftOrder
      ? 'shipping'
      : gift.shipsTo === GiftShipsTo.RECIPIENT
        ? 'hardware_first_checkout_gift_recipient_address'
        : 'hardware_first_checkout_gift_your_address';
  // the page draws this button under the summary on desktop; it submits this form by id
  const publishSubmitState = useContext(StripeCheckoutSubmitContext);
  useEffect(() => {
    publishSubmitState?.({
      label: submitLabel,
      disabled: isSubmitting || !!pricingError,
      loading: isSubmitting,
      hidden: paypalSelected,
    });
  }, [publishSubmitState, submitLabel, isSubmitting, pricingError, paypalSelected]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
    (event) => {
      event.preventDefault();
      void submitForm();
    },
    [submitForm],
  );

  const addressElement = (
    <AddressElement
      key={`address-${addressKey}`}
      options={{
        mode: cardOnly ? 'billing' : 'shipping',
        autocomplete: addressAutocomplete,
        display: { name: 'split' },
        fields: { phone: 'always' },
        // a gift recipient's phone only feeds the delivery text, which falls back to the buyer's
        validation: { phone: { required: billsBuyer ? 'never' : 'always' } },
        allowedCountries: [country],
        // the buyer's prefill never seeds the recipient's address on a ship-to-recipient gift
        defaultValues: placesAddress
          ? {
              ...(billsBuyer ? {} : prefillContact),
              address: {
                line1: placesAddress.line1,
                line2: placesAddress.line2,
                city: placesAddress.city,
                state: placesAddress.state,
                postal_code: placesAddress.postalCode,
                country,
              },
            }
          : {
              ...(billsBuyer ? {} : prefillContact),
              address: { ...(billsBuyer ? {} : prefill?.address), country },
            },
      }}
      onChange={handleAddressChange}
      onReady={(element) => {
        recipientAddressElementRef.current = billsBuyer ? element : null;
      }}
    />
  );

  return (
    <form id={STRIPE_CHECKOUT_FORM_ID} onSubmit={handleSubmit} className={styles.form}>
      <div className={classNames(styles.formContent, { [styles.formContentV2]: checkoutV2 })}>
        <div className={sectionClass} ref={emailSectionRef}>
          <h4>{t(giftOrder || giftCards.length > 0 ? 'hardware_first_checkout_your_email' : 'enter_email')}</h4>
          <LinkAuthenticationElement
            options={prefill?.email ? { defaultValues: { email: prefill.email } } : undefined}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            onReady={() => {
              if (elements && autoFocusEmail) {
                requestAnimationFrame(() => {
                  const linkEl = elements.getElement('linkAuthentication');
                  linkEl?.focus();
                });
              }
            }}
          />
        </div>

        {/* progressive disclosure: disable sections below email until it's valid.
            uses opacity + pointerEvents instead of conditional rendering to keep Stripe
            elements mounted — unmounting would lose entered data and require re-init. */}
        <div style={!emailComplete && !checkoutV2 ? { opacity: 0.4, pointerEvents: 'none' } : undefined}>
          {giftOrder && (
            <div className={sectionClass} ref={giftSectionRef}>
              <h4>{t('hardware_first_checkout_gift_options')}</h4>
              <GiftOptions
                value={gift}
                errors={giftOptionsErrors(gift)}
                onChange={handleGiftChange}
                showEmailError={giftEmailTouched}
                onEmailBlur={() => setGiftEmailTouched(true)}
                mixed={giftMixed}
              />
            </div>
          )}
          {giftCards.map((card, index) => (
            <div key={card.cartItemId} className={sectionClass} ref={index === 0 ? giftCardSectionRef : undefined}>
              <GiftCardFields
                amount={requiresShippingDetails ? giftCardAmounts[card.cartItemId] : undefined}
                delivery={card.giftCardDelivery}
                onChange={(delivery) => onGiftCardDeliveryChange?.(card.cartItemId, delivery)}
                showErrors={giftCardErrorsShown}
              />
            </div>
          ))}
          {billsBuyer && (
            <GiftBillingAddress
              country={country}
              onChange={handleGiftBillingChange}
              className={sectionClass}
              sectionRef={giftBillingSectionRef}
            >
              <label className={giftStyles.sameAs}>
                <input
                  type="checkbox"
                  className={giftStyles.check}
                  checked={sameAsBilling}
                  onChange={(event) => handleSameAsBillingChange(event.currentTarget.checked)}
                />
                {t('hardware_first_checkout_gift_recipient_same_as_billing')}
              </label>
            </GiftBillingAddress>
          )}
          <div
            className={sectionClass}
            ref={addressSectionRef}
            style={shipsToBilling ? { display: 'none' } : undefined}
          >
            <h4>{t(addressHeadingKey)}</h4>
            {offerPlacesAutocomplete && placesInputVisible && (
              <PlacesAutocompleteInput country={country} onSelect={handlePlacesSelect} />
            )}
            {offerPlacesAutocomplete && !placesInputVisible && (
              <button type="button" className={styles.searchAgain} onClick={handleSearchAgain}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                {t('search_address')}
              </button>
            )}
            {billsBuyer ? (
              // link fills a shipping address element in its group with the buyer's own saved address
              <StripeElementsProvider mode="setup" appearance={checkoutV2 ? HARDWARE_FIRST_GIFT_APPEARANCE : undefined}>
                {addressElement}
              </StripeElementsProvider>
            ) : (
              addressElement
            )}
            {requiresShippingDetails && giftCards.length > 0 && (
              <p className={giftStyles.note}>
                <InfoIcon />
                {t('hardware_first_checkout_gift_card_only_tracker_ships')}
              </p>
            )}
          </div>

          {(stripeReady || checkoutV2) && (
            // For digital-only carts (no physical items to ship) the shipping method selector
            // makes no sense, so it's hidden rather than unmounted. Keeping it mounted lets it
            // auto-select the default (free ground) shipping code that submission still requires,
            // matching the file's existing pattern of hiding rather than unmounting Stripe-adjacent
            // sections to avoid losing state. Uses an inline display:none (not the `hidden` attribute)
            // because .section composes a `display: flex` rule that would override `hidden`.
            <div
              className={sectionClass}
              ref={shippingSectionRef}
              style={requiresShippingDetails ? undefined : { display: 'none' }}
            >
              <ShippingOptionsList
                anyPreorder={anyPreorder}
                canExpedite={canExpedite}
                compact={checkoutV2}
                gift={giftOrder || giftCards.length > 0}
                currentlySelected={selectedShippingCode}
                onSelect={handleShippingCodeChange}
              />
            </div>
          )}

          <div className={sectionClass} ref={paymentSectionRef}>
            <h4>{t('payment')}</h4>
            <div ref={paymentElementRef} style={heldHeight ? { minHeight: heldHeight } : undefined}>
              <PaymentElement
                // a new element for each change to what `wallets` must be, since stripe reads it once
                key={`${trialPending ? 'trial-pending' : 'priced'}-${walletsRevealed ? 'wallets' : 'form'}`}
                onLoadError={() => {
                  // hotjar can't see inside the stripe iframe, so replays show a blank
                  // box whether the form rendered or not. these events disambiguate.
                  hotjar.event('stripe_payment_element_load_error');
                }}
                onReady={() => {
                  hotjar.event('stripe_payment_element_ready');
                  setStripeReady(true);
                  setHeldHeight(undefined);
                  // give saved payment methods time to load before deciding
                  // whether to show paypal. 300ms is enough for stripe to populate
                  // a saved card and fire onChange with complete=true.
                  setTimeout(() => {
                    settledRef.current = true;
                    if (!hasSavedPaymentRef.current) {
                      setPaymentAccordionOpen(true);
                    }
                  }, 300);
                }}
                onChange={(event) => {
                  setSelectedPaymentMethodType(event.value?.type ?? null);

                  // when a Link-saved payment is dismissed (complete → incomplete),
                  // Stripe internally focuses the email element which scrolls the
                  // browser to the top of the form. counteract by scrolling back
                  // to the payment section after a short delay.
                  if (paymentComplete && !event.complete && paymentSectionRef.current) {
                    setTimeout(() => {
                      paymentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }, 50);
                  }
                  setPaymentComplete(event.complete);

                  // track saved payment detection. if complete fires before settle
                  // timeout, a saved card was loaded. after settling, any transition
                  // to incomplete means user clicked "Change" to expand the accordion.
                  if (event.complete) {
                    hasSavedPaymentRef.current = true;
                  }
                  if (settledRef.current && hasSavedPaymentRef.current && !event.complete) {
                    setPaymentAccordionOpen(true);
                  }

                  // deselect paypal when user clicks a stripe payment method
                  if (paypalSelected) setPaypalSelected(false);
                }}
                options={{
                  layout: {
                    type: 'accordion',
                    defaultCollapsed: false,
                    radios: true,
                    spacedAccordionItems: true,
                  },
                  ...applePayWalletOptions(trialPending, applePayRecurring),
                  ...(!walletsRevealed && { wallets: { applePay: 'never' as const, googlePay: 'never' as const } }),
                  // billing details come from our own email/address fields, passed via
                  // payment_method_data at token creation. collecting them here too would both
                  // duplicate inputs and conflict with the passed values; disabling collection also
                  // means redirect methods (klarna) receive the full contact info on their hosted
                  // page instead of asking the customer to re-enter it.
                  fields: { billingDetails: 'never' },
                  // billing fields are hidden, so this only seeds Link's phone number. react-stripe-js
                  // pushes option changes through element.update(), so it follows the address field.
                  defaultValues: {
                    billingDetails: { ...(addressPhone && { phone: addressPhone }), address: { country } },
                  },
                }}
              />
            </div>
            {showPayPal && paymentAccordionOpen && walletsReady && (
              <div
                className={classNames(styles.paypalAccordion, {
                  [styles.paypalAccordionSelected]: paypalSelected,
                  [styles.paypalAccordionV2]: checkoutV2,
                })}
                role="button"
                tabIndex={0}
                onClick={() => setPaypalSelected(true)}
                onKeyDown={(e) => e.key === 'Enter' && setPaypalSelected(true)}
              >
                <div className={styles.paypalAccordionHeader}>
                  <input type="radio" checked={paypalSelected} readOnly tabIndex={-1} />
                  <PayPalIcon className={styles.paypalIcon} />
                  <span>{t('paypal')}</span>
                </div>
                {paypalSelected && (
                  <div className={styles.paypalAccordionContent}>
                    <PayPalButton
                      events={events.payment}
                      onComplete={handlePayPalTokenReceived}
                      onError={(err) => setError(err.message)}
                      onStart={clearError}
                      disabled={!!pricingError}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {checkoutV2 && (
          <div className={styles.foot}>
            <MarketingOptInCheckbox />
          </div>
        )}

        {displayError && <ErrorMessage errors={[displayError]} errorID={errorID} />}
      </div>

      {!paypalSelected && (
        <div className={classNames(styles.actionContainer, { [styles.actionContainerV2]: checkoutV2 })}>
          <ActionContainer>
            <Button disabled={isSubmitting || !!pricingError} loading={isSubmitting} type="submit">
              {submitLabel}
            </Button>
          </ActionContainer>
        </div>
      )}
    </form>
  );
}
